// TODO: This is a replicata of EV hook. Duplicated to help proceed.

import gql from 'not-graphql-tag';
import {
  CreateApplicationEvOutput,
  CancelApplicationInput,
  CancelApplicationOutput,
  MutationCancelApplicationArgs,
  GetApplicationsInput,
  GetApplicationsOutput,
  QueryGetApplicationsArgs,
  GetApplicationOutput,
  GetApplicationInput,
  QueryGetApplicationArgs,
  MutationCreateApplicationEvArgs,
  CreateApplicationEvInput,
  AcceptQuoteEvInput,
  MutationAcceptQuoteEvArgs,
  AcceptQuoteEvOutput,
  RejectQuoteInput,
  MutationRejectQuoteArgs,
  RejectQuoteOutput,
  CreateApplicationErInput,
  CreateApplicationErOutput,
  MutationCreateApplicationErArgs,
  SubmitApplicationErInput,
  SubmitApplicationErOutput,
  MutationSubmitApplicationErArgs,
} from '../../__generated__/pgeplus-types';
import {
  applicationERFragment,
  applicationFragment,
} from '../../components/pge-plus-common/queries/fragments';

import { useApolloClient, useMutation } from '@apollo/react-hooks';

export const createApplicationMutation = gql`
  mutation CreateApplicationER($input: CreateApplicationERInput!) {
    createApplicationER(input: $input) {
      ineligibilityReasons {
        message
        reference {
          number
          category
        }
        code
        details
      }
      application {
        ...applicationFragment
        ...applicationERFragment
      }
    }
  }

  ${applicationFragment}
  ${applicationERFragment}
`;

export const getApplicationsByAccountQuery = gql`
  query GetApplications($input: GetApplicationsInput!) {
    getApplications(input: $input) {
      applications {
        id
        accountId
        premiseId
        status
        statusReason
        applicationNo
      }
    }
  }
`;

export const getApplicationDetailsQuery = gql`
  query GetApplicationData($input: GetApplicationInput!) {
    getApplication(input: $input) {
      application {
        ...applicationFragment
        ...applicationERFragment
      }
    }
  }

  ${applicationFragment}
  ${applicationERFragment}
`;

export const quoteFragment = gql`
  fragment quoteFragment on Quote {
    ... on QuoteEV {
      id
      version
      jobId
      status
      total
      createdAt
      updatedAt
      data {
        lineItems {
          ... on LineItemDetail {
            key
            name
            value
          }
          ... on LineItemCost {
            key
            name
            category
            amount
          }
          ... on LineItemProduct {
            key
            name
            value
            amount
          }
          ... on LineItemRebate {
            key
            name
            amount
            rebateItems {
              panelUpgrade
              evCharger
            }
          }
          ... on LineItemProgram {
            key
            name
            value
          }
        }
        confirmContractorRebatePayee
        confirmEVVehicleOwner
        acknowledgeTerms
      }
    }
  }
`;

export const jobFragment = gql`
  fragment jobFragment on Job {
    id
    serviceProviderId
    applicationId
    applicationNo
    leadType
    status
    createdAt
    updatedAt
    quote {
      ...quoteFragment
    }
    jobDetails {
      id
      jobId
      jobDate
      total
      comments
      createdAt
      updatedAt
      data {
        images {
          key
          name
          objectId
        }
      }
    }
    jobLocation {
      id
      jobId
      premiseId
      addressLine1
      city
      state
      postalCode
      firstName
      lastName
      middleName
      phone
      email
    }
    serviceProviderInfo {
      firstName
      lastName
      phone
    }
  }

  ${quoteFragment}
`;

export const getApplicationsByAccountQueryWithDetails = gql`
  query GetApplications($input: GetApplicationsInput!) {
    getApplications(input: $input) {
      applications {
        id
        accountId
        premiseId
        status
        statusReason
        applicationNo
        ...applicationFragment
        ...applicationERFragment
        job {
          ...jobFragment
        }
      }
    }
  }

  ${applicationFragment}
  ${applicationERFragment}
  ${jobFragment}
`;

export const getRichApplicationDetailsQuery = gql`
  query Query($input: GetApplicationInput!) {
    getApplication(input: $input) {
      application {
        ...applicationFragment
        ...applicationERFragment
        job {
          ...jobFragment
        }
      }
    }
  }

  ${applicationFragment}
  ${applicationERFragment}
  ${jobFragment}
`;

export const cancelApplicationMutation = gql`
  mutation cancelApplication($input: CancelApplicationInput!) {
    cancelApplication(input: $input) {
      canceled
    }
  }
`;

export const acceptQuoteMutation = gql`
  mutation acceptQuote($input: AcceptQuoteEVInput!) {
    acceptQuoteEV(input: $input) {
      quote {
        ... on QuoteEV {
          version
          data {
            confirmEVVehicleOwner
            acknowledgeTerms
            confirmContractorRebatePayee
          }
        }
      }
    }
  }
`;

export const rejectQuoteMutation = gql`
  mutation RejectQuote($input: RejectQuoteInput!) {
    rejectQuote(input: $input) {
      quote {
        ... on QuoteEV {
          version
        }
      }
    }
  }
`;

export const getApplicationsByAccountWithAddressQuery = gql`
  query GetApplications($input: GetApplicationsInput!) {
    getApplications(input: $input) {
      applications {
        ... on ApplicationER {
          details {
            ... on SectionERAccountInfo {
              data {
                address {
                  addressLine1
                  city
                  postal
                  state
                }
              }
              sectionType
            }
          }
        }
        accountId
        applicationNo
        id
        status
      }
    }
  }
`;
export const submitApplicationMutation = gql`
  mutation SubmitApplicationER($input: SubmitApplicationERInput!) {
    submitApplicationER(input: $input) {
      ineligibilityReasons {
        message
        reference {
          number
          category
        }
        code
        details
      }
      application {
        ...applicationFragment
      }
    }
  }

  ${applicationFragment}
`;

const useERApplicationApi = () => {
  const apolloClient = useApolloClient();

  const createApplication = async (input: CreateApplicationErInput) => {
    return apolloClient.mutate<
      { createApplicationER: CreateApplicationErOutput },
      MutationCreateApplicationErArgs
    >({
      mutation: createApplicationMutation,
      variables: {
        input,
      },
      context: { pgePlus: true },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });
  };

  const getApplicationsByAccount = async (input: GetApplicationsInput) => {
    return apolloClient.query<
      { getApplications: GetApplicationsOutput },
      QueryGetApplicationsArgs
    >({
      query: getApplicationsByAccountQuery,
      variables: {
        input,
      },
      context: { pgePlus: true },
      fetchPolicy: 'network-only',
    });
  };

  const getApplicationsByAccountWithDetails = async (
    input: GetApplicationsInput,
    query = getApplicationsByAccountQueryWithDetails,
  ) => {
    return apolloClient.query<
      { getApplications: GetApplicationsOutput },
      QueryGetApplicationsArgs
    >({
      query,
      variables: {
        input,
      },
      context: { pgePlus: true },
      fetchPolicy: 'no-cache',
    });
  };

  //Must use 'applicationNo' as applicationId input
  const getApplicationDetails = async (
    input: GetApplicationInput,
    isJobDataRequired: boolean = false,
  ) => {
    return await apolloClient.query<
      { getApplication: GetApplicationOutput },
      QueryGetApplicationArgs
    >({
      query: isJobDataRequired
        ? getRichApplicationDetailsQuery
        : getApplicationDetailsQuery,
      variables: {
        input,
      },
      context: { pgePlus: true },
      fetchPolicy: 'no-cache',
    });
  };

  const cancelApplication = async (input: CancelApplicationInput) => {
    return apolloClient.mutate<
      { cancelApplication: CancelApplicationOutput },
      MutationCancelApplicationArgs
    >({
      mutation: cancelApplicationMutation,
      variables: { input },
      context: { pgePlus: true },
      errorPolicy: 'all',
    });
  };

  const acceptQuote = async (input: AcceptQuoteEvInput) => {
    return apolloClient.mutate<
      { acceptQuote: AcceptQuoteEvOutput },
      MutationAcceptQuoteEvArgs
    >({
      mutation: acceptQuoteMutation,
      variables: { input },
      context: { pgePlus: true },
      errorPolicy: 'all',
    });
  };

  const declineQuote = async (input: RejectQuoteInput) => {
    return apolloClient.mutate<
      { acceptQuote: RejectQuoteOutput },
      MutationRejectQuoteArgs
    >({
      mutation: rejectQuoteMutation,
      variables: { input },
      context: { pgePlus: true },
      errorPolicy: 'all',
    });
  };

  const submitApplication = async (input: SubmitApplicationErInput) => {
    return apolloClient.mutate<
      { submitApplicationER: SubmitApplicationErOutput },
      MutationSubmitApplicationErArgs
    >({
      mutation: submitApplicationMutation,
      variables: { input },
      context: { pgePlus: true },
      errorPolicy: 'all',
    });
  };

  return {
    createApplication, //Mutation
    getApplicationsByAccount, //Query
    getApplicationsByAccountWithDetails, //Query
    getApplicationDetails, //Query
    cancelApplication, //Mutation
    acceptQuote, //Mutation
    declineQuote, //Mutation
    submitApplication, //Mutation
  };
};

export default useERApplicationApi;
